import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Image,
  Drawer,
  Empty,
  Tooltip,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import axios from '../../utils/axios';
import { getUploadURL } from '../../utils/fileUtils';
import { formatMoney } from '../../utils/formatMoney';
import { formatDateTime, fromNow } from '../../utils/dateFormat';
import { showApiError } from '../../utils/apiError';
import {
  PageHeader,
  StatCard,
  StatGrid,
  TableCard,
  Section,
  statusRenderer,
  REVIEW_STATUS,
  DRAWER_WIDTH,
  MODAL_WIDTH,
  tableDefaults,
  paginationDefaults,
} from '../../components/admin';

const { Text } = Typography;
const { TextArea } = Input;

// Repayments have one extra status: `processing` while the approval is being
// applied to the client's credit.
const REPAYMENT_STATUS = {
  ...REVIEW_STATUS,
  processing: { label: 'Processing', tone: 'info' },
};
const renderStatus = statusRenderer(REPAYMENT_STATUS);

const STATUS_TABS = [
  { key: 'pending', label: 'Pending review', tone: 'warning', icon: <ClockCircleOutlined /> },
  { key: 'processing', label: 'Processing', tone: 'primary', icon: <SyncOutlined /> },
  { key: 'approved', label: 'Approved', tone: 'success', icon: <CheckCircleOutlined /> },
  { key: 'rejected', label: 'Rejected', tone: 'danger', icon: <CloseCircleOutlined /> },
  { key: 'all', label: 'All requests', tone: 'default', icon: <InboxOutlined /> },
];

const PAYMENT_METHOD_LABELS = {
  bank_transfer: 'Bank transfer',
  wire_transfer: 'Wire transfer',
  check: 'Check',
  online_payment: 'Online payment',
  manual_transfer: 'Manual transfer',
};

const paymentMethodLabel = (method) =>
  PAYMENT_METHOD_LABELS[method] || (method ? String(method).replace(/_/g, ' ') : '-');

const REJECTION_REASONS = [
  { value: 'insufficient_proof', label: 'Insufficient payment proof' },
  { value: 'invalid_amount', label: 'Invalid payment amount' },
  { value: 'payment_not_received', label: 'Payment not received' },
  { value: 'duplicate_request', label: 'Duplicate request' },
  { value: 'client_account_issue', label: 'Client account issue' },
  { value: 'other', label: 'Other (explain in the note)' },
];

const rejectionReasonLabel = (value) =>
  REJECTION_REASONS.find((reason) => reason.value === value)?.label || value;

const getRequestId = (req) => req?.id || req?._id;

const fileSize = (size) =>
  Number.isFinite(Number(size)) ? `${(Number(size) / 1024).toFixed(1)} KB` : null;

// The list endpoint only counts the status it was asked for, so a tile shows
// a number once that status (or "All") has been loaded.
const UNKNOWN_COUNTS = {
  pending: undefined,
  processing: undefined,
  approved: undefined,
  rejected: undefined,
  all: undefined,
};

const RepaymentRequestManagement = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decision, setDecision] = useState(null); // 'approve' | 'reject' | null
  const [reviewForm] = Form.useForm();
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('pending');
  const [counts, setCounts] = useState(UNKNOWN_COUNTS);
  const [pager, setPager] = useState({ current: 1, pageSize: 10 });

  // Returns the rows so a reviewer can move on to the next pending request.
  const fetchRequests = useCallback(async (status) => {
    try {
      setLoading(true);
      // The API's `summary` only counts the status asked for, so a filtered
      // view also fetches the unfiltered list once to fill every tile.
      const [response, allResponse] = await Promise.all([
        axios.get('/admin/repayment-requests', {
          params: { status: status === 'all' ? undefined : status },
        }),
        status === 'all' ? null : axios.get('/admin/repayment-requests').catch(() => null),
      ]);
      const rows = response.data.requests || [];
      setRequests(rows);
      const countSource = status === 'all' ? response.data : allResponse?.data;
      if (countSource) {
        const summary = countSource.summary || {};
        setCounts({
          all: summary.total ?? (countSource.requests || []).length,
          pending: summary.pending ?? 0,
          processing: summary.processing ?? 0,
          approved: summary.approved ?? 0,
          rejected: summary.rejected ?? 0,
        });
      } else {
        setCounts((prev) => ({ ...prev, [status]: rows.length }));
      }
      return rows;
    } catch (error) {
      console.error('Failed to fetch repayment requests:', error);
      showApiError(error, 'Failed to load repayment requests');
    } finally {
      setLoading(false);
    }
    return [];
  }, []);

  useEffect(() => {
    fetchRequests(filter);
  }, [fetchRequests, filter]);

  const selectFilter = (key) => {
    setFilter(key);
    setPager((prev) => ({ ...prev, current: 1 }));
  };

  const openRequest = (record) => {
    setSelectedRequest(record);
  };

  const closeDrawer = () => {
    setSelectedRequest(null);
    setDecision(null);
    reviewForm.resetFields();
  };

  const openDecision = (action) => {
    reviewForm.resetFields();
    setDecision(action);
  };

  // Approve or reject, then open the next pending request so a reviewer can
  // work through the queue without going back to the list each time.
  const handleDecision = async () => {
    if (processing || !selectedRequest || !decision) return;
    const action = decision;
    let values;
    try {
      // Reject validates the rejection reason; approve only validates the
      // note, so the reason stays optional during approval.
      values = await reviewForm.validateFields(
        action === 'reject' ? ['rejectionReason', 'reviewNotes'] : ['reviewNotes']
      );
    } catch {
      return;
    }

    const reviewed = selectedRequest;
    const reviewedId = getRequestId(reviewed);
    const indexInList = requests.findIndex((r) => getRequestId(r) === reviewedId);
    const verb = action === 'approve' ? 'approved' : 'rejected';
    const name = reviewed.requestNumber || 'Request';

    try {
      setProcessing(true);
      if (action === 'reject') {
        await axios.post(`/admin/repayment-requests/${reviewedId}/reject`, {
          rejectionReason: values.rejectionReason,
          reviewNotes: values.reviewNotes || '',
        });
      } else {
        await axios.post(`/admin/repayment-requests/${reviewedId}/approve`, {
          reviewNotes: values.reviewNotes || '',
        });
      }

      setDecision(null);
      reviewForm.resetFields();
      setCounts((prev) =>
        typeof prev[verb] === 'number' ? { ...prev, [verb]: prev[verb] + 1 } : prev
      );

      const rows = await fetchRequests(filter);
      const nextPending =
        filter === 'pending'
          ? rows.filter((r) => r.status === 'pending' && getRequestId(r) !== reviewedId)
          : [];
      if (nextPending.length > 0) {
        const next = nextPending[Math.min(Math.max(indexInList, 0), nextPending.length - 1)];
        setSelectedRequest(next);
        message.success(`${name} ${verb}. Showing the next pending request.`);
      } else {
        setSelectedRequest(null);
        message.success(
          filter === 'pending'
            ? `${name} ${verb}. No more repayments waiting for review.`
            : `Repayment request ${verb}`
        );
      }
    } catch (error) {
      console.error(`Failed to ${action} request:`, error);
      showApiError(error, `Failed to ${action} request`);
    } finally {
      setProcessing(false);
    }
  };

  const columns = [
    {
      title: 'Request',
      dataIndex: 'requestNumber',
      key: 'requestNumber',
      width: 200,
      render: (text, record) => (
        <div>
          <Text className="cell-mono" copyable={text ? { text } : false}>
            {text || getRequestId(record)}
          </Text>
          <div className="cell-sub">{fromNow(record.submittedAt)}</div>
        </div>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'clientName',
      key: 'clientName',
      render: (name, record) => (
        <div>
          <div className="cell-main">{name || '-'}</div>
          <div className="cell-sub">{record.clientEmail}</div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      width: 160,
      render: (amount, record) => (
        <span className="cell-money">{formatMoney(amount, record.currency)}</span>
      ),
    },
    {
      title: 'Payment method',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 170,
      render: (method, record) => (
        <div>
          <div>{paymentMethodLabel(method)}</div>
          {record.paymentReference ? (
            <div className="cell-sub" title={record.paymentReference}>
              Ref {record.paymentReference}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Proof',
      key: 'proof',
      width: 80,
      align: 'center',
      render: (_, record) =>
        record.paymentProof?.filename ? (
          <Tooltip title={record.paymentProof.originalName || 'Payment proof attached'}>
            <FileTextOutlined style={{ color: 'var(--c-primary)' }} />
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: renderStatus,
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 160,
      render: formatDateTime,
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      fixed: 'right',
      align: 'right',
      render: (_, record) => (
        <Button
          type={record.status === 'pending' ? 'primary' : 'default'}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openRequest(record);
          }}
        >
          {record.status === 'pending' ? 'Review' : 'View'}
        </Button>
      ),
    },
  ];

  const activeTab = STATUS_TABS.find((t) => t.key === filter) || STATUS_TABS[0];
  const proof = selectedRequest?.paymentProof;
  const proofUrl = proof?.filename ? getUploadURL('repayment-proofs', proof.filename) : null;
  const clientName = selectedRequest?.clientName || 'this client';
  const amountText = selectedRequest
    ? formatMoney(selectedRequest.amount, selectedRequest.currency)
    : '';

  return (
    <div>
      <PageHeader
        title="Repayment requests"
        subtitle="Check the payment proof, then approve to reduce the client's outstanding credit or reject with a reason."
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => fetchRequests(filter)} loading={loading}>
            Refresh
          </Button>
        }
      />

      <StatGrid>
        {STATUS_TABS.map((tab) => (
          <StatCard
            key={tab.key}
            label={tab.label}
            value={typeof counts[tab.key] === 'number' ? counts[tab.key] : <span className="muted">–</span>}
            icon={tab.icon}
            tone={tab.tone}
            highlight={tab.key === 'pending' && counts.pending > 0}
            active={filter === tab.key}
            onClick={() => selectFilter(tab.key)}
          />
        ))}
      </StatGrid>

      <TableCard title={activeTab.label} count={requests.length}>
        <Table
          {...tableDefaults}
          columns={columns}
          dataSource={requests}
          rowKey={(r) => r?.id || r?._id}
          loading={loading}
          rowClassName={() => 'row-clickable'}
          onRow={(record) => ({ onClick: () => openRequest(record) })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  filter === 'pending' ? 'No repayments waiting for review' : 'No repayment requests'
                }
              />
            ),
          }}
          pagination={{
            ...paginationDefaults,
            ...pager,
            onChange: (current, pageSize) => setPager({ current, pageSize }),
          }}
        />
      </TableCard>

      <Drawer
        title={
          selectedRequest ? (
            <Space size={10}>
              <span>Repayment {selectedRequest.requestNumber || ''}</span>
              {renderStatus(selectedRequest.status)}
            </Space>
          ) : (
            'Repayment request'
          )
        }
        open={!!selectedRequest}
        onClose={closeDrawer}
        width={DRAWER_WIDTH.lg}
        destroyOnHidden
        footer={
          selectedRequest?.status === 'pending' ? (
            <div className="sheet-footer">
              <Text type="secondary">Approving reduces the client's outstanding credit immediately.</Text>
              <div className="sheet-footer__actions">
                <Button danger icon={<CloseOutlined />} onClick={() => openDecision('reject')}>
                  Reject
                </Button>
                <Button type="primary" icon={<CheckOutlined />} onClick={() => openDecision('approve')}>
                  Approve
                </Button>
              </div>
            </div>
          ) : null
        }
      >
        {selectedRequest && (
          <>
            <Section>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <div className="muted" style={{ fontSize: 13 }}>Amount</div>
                  <div className="cell-money" style={{ fontSize: 26, fontWeight: 650, lineHeight: '34px' }}>
                    {amountText}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="muted" style={{ fontSize: 13 }}>Client</div>
                  <div className="cell-main">{selectedRequest.clientName || '-'}</div>
                  <div className="cell-sub">{selectedRequest.clientEmail}</div>
                </div>
              </div>
            </Section>

            <Section title="Request details">
              <dl className="kv">
                <dt>Request number</dt>
                <dd>
                  <Text copyable className="cell-mono">
                    {selectedRequest.requestNumber || getRequestId(selectedRequest)}
                  </Text>
                </dd>
                {selectedRequest.repaymentType ? (
                  <>
                    <dt>Repayment type</dt>
                    <dd>{String(selectedRequest.repaymentType).replace(/_/g, ' ')}</dd>
                  </>
                ) : null}
                <dt>Payment method</dt>
                <dd>{paymentMethodLabel(selectedRequest.paymentMethod)}</dd>
                <dt>Payment reference</dt>
                <dd>
                  {selectedRequest.paymentReference ? (
                    <Text copyable>{selectedRequest.paymentReference}</Text>
                  ) : (
                    '-'
                  )}
                </dd>
                <dt>Submitted</dt>
                <dd>
                  {formatDateTime(selectedRequest.submittedAt)}{' '}
                  <span className="muted">({fromNow(selectedRequest.submittedAt)})</span>
                </dd>
              </dl>
            </Section>

            <Section
              title="Payment proof"
              description={
                proof?.filename
                  ? [proof.originalName, fileSize(proof.size)].filter(Boolean).join(' · ')
                  : undefined
              }
              extra={
                proofUrl ? (
                  <a
                    href={proofUrl}
                    download={proof.originalName || proof.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button size="small" icon={<DownloadOutlined />}>
                      Download
                    </Button>
                  </a>
                ) : null
              }
            >
              {!proofUrl ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payment proof uploaded" />
              ) : proof.mimeType?.startsWith('image/') ? (
                <div
                  style={{
                    background: 'var(--c-bg-subtle)',
                    border: '1px solid var(--c-border-soft)',
                    borderRadius: 'var(--radius)',
                    padding: 8,
                    textAlign: 'center',
                  }}
                >
                  <Image
                    src={proofUrl}
                    alt="Payment proof"
                    style={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain' }}
                  />
                  <div className="cell-sub" style={{ marginTop: 6 }}>
                    Click the image to enlarge
                  </div>
                </div>
              ) : proof.mimeType === 'application/pdf' ? (
                <iframe
                  src={proofUrl}
                  title="Payment proof PDF"
                  style={{
                    width: '100%',
                    height: 460,
                    border: '1px solid var(--c-border-soft)',
                    borderRadius: 'var(--radius)',
                  }}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`No preview for ${proof.mimeType || 'this file type'}. Download it to check.`}
                />
              )}
            </Section>

            {selectedRequest.notes ? (
              <Section title="Client note">
                <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{selectedRequest.notes}</p>
              </Section>
            ) : null}

            {selectedRequest.status !== 'pending' ? (
              <Section title="Review">
                <dl className="kv">
                  <dt>Decision</dt>
                  <dd>{renderStatus(selectedRequest.status)}</dd>
                  <dt>Reviewed</dt>
                  <dd>{formatDateTime(selectedRequest.reviewedAt)}</dd>
                  {selectedRequest.reviewedBy ? (
                    <>
                      <dt>Reviewed by</dt>
                      <dd>{selectedRequest.reviewedBy}</dd>
                    </>
                  ) : null}
                  {selectedRequest.rejectionReason ? (
                    <>
                      <dt>Rejection reason</dt>
                      <dd>{rejectionReasonLabel(selectedRequest.rejectionReason)}</dd>
                    </>
                  ) : null}
                  {selectedRequest.reviewNotes ? (
                    <>
                      <dt>Notes</dt>
                      <dd style={{ whiteSpace: 'pre-wrap' }}>{selectedRequest.reviewNotes}</dd>
                    </>
                  ) : null}
                </dl>
              </Section>
            ) : null}
          </>
        )}
      </Drawer>

      <Modal
        title={decision === 'reject' ? 'Reject repayment request' : 'Approve repayment request'}
        open={!!decision && !!selectedRequest}
        onCancel={() => setDecision(null)}
        width={MODAL_WIDTH.sm}
        destroyOnHidden
        maskClosable={!processing}
        footer={[
          <Button key="cancel" onClick={() => setDecision(null)} disabled={processing}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger={decision === 'reject'}
            loading={processing}
            onClick={handleDecision}
          >
            {decision === 'reject' ? 'Reject request' : 'Approve repayment'}
          </Button>,
        ]}
      >
        {selectedRequest ? (
          <>
            <p style={{ marginTop: 0 }}>
              {decision === 'reject' ? (
                <>
                  The request for <strong>{amountText}</strong> is marked as rejected.{' '}
                  <strong>{clientName}</strong>'s outstanding credit does not change.
                </>
              ) : (
                <>
                  <strong>{amountText}</strong> will be deducted from <strong>{clientName}</strong>'s
                  outstanding credit.
                </>
              )}
            </p>
            <Form form={reviewForm} layout="vertical" requiredMark={false}>
              {decision === 'reject' ? (
                <Form.Item
                  label="Rejection reason"
                  name="rejectionReason"
                  rules={[
                    ({ getFieldValue: _ }) => ({
                      validator: (_rule, value) => {
                        // Only rendered and validated in the reject dialog
                        // (validateFields(['rejectionReason', 'reviewNotes']));
                        // approval validates ['reviewNotes'] only.
                        if (!value) {
                          return Promise.reject(
                            new Error('Please select a rejection reason'),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Select
                    placeholder="Select a reason"
                    allowClear
                    options={REJECTION_REASONS}
                  />
                </Form.Item>
              ) : null}
              <Form.Item label="Note (optional)" name="reviewNotes" style={{ marginBottom: 0 }}>
                <TextArea
                  rows={3}
                  autoFocus={decision !== 'reject'}
                  placeholder={
                    decision === 'reject'
                      ? 'e.g. The bank slip shows a different amount'
                      : 'Saved with the review'
                  }
                />
              </Form.Item>
            </Form>
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default RepaymentRequestManagement;
